import { Building2, CalendarCheck2, CalendarClock } from "lucide-react";
import { formatCurrency } from "@wtc/ui";
import type { Deals } from "@/src/gql/graphql";
import { hasBeenFollowedUpThisWeek } from "@/lib/utils/follow-up-tagging";
import type { DetailHeaderModel, HeaderFact, HeaderMetric, HeaderSubtitleSegment } from "../model";
import { formatHeaderDate } from "../model";

/**
 * Fields the deal header reads. Guarded against the GetDeal query by
 * tests/utils/detail-header-model-coverage.test.ts — a field listed here that
 * the detail query stops selecting fails the build instead of rendering blank.
 */
export const DEAL_HEADER_FIELDS = [
  "stage",
  "revenue",
  "currency",
  "monthly_amount",
  "is_recurring",
  "close_probability",
  "close_date",
  "company_id",
  "company_name",
  // Week-aware follow-up tags (`followed-up-{YYYY-W##}`) — drives the
  // "Followed up this week" vs "Needs follow-up" header indicator. Same source
  // the Slack follow-up reminder cron + "Mark Followed Up" action use.
  "tags",
] as const satisfies readonly (keyof Deals)[];

export type DealHeaderData = Pick<Deals, (typeof DEAL_HEADER_FIELDS)[number]>;

/**
 * Header elements that are edited IN the header (and therefore dropped from the
 * inline edit panel). 1:1 scalar columns only — never derived values (the
 * follow-up fact) or the company relation. `keyof Deals` ⇒ schema-checked.
 */
export const DEAL_HEADER_EDITABLE_FIELDS = [
  "revenue",
  "monthly_amount",
  "close_probability",
  "close_date",
] as const satisfies readonly (keyof Deals)[];
type DealHeaderEditableField = (typeof DEAL_HEADER_EDITABLE_FIELDS)[number];

export function buildDealHeaderModel(deal: DealHeaderData): DetailHeaderModel {
  const subtitleSegments: HeaderSubtitleSegment[] = [];
  if (deal.company_name) {
    subtitleSegments.push({
      text: deal.company_name,
      href: deal.company_id ? `/companies/${deal.company_id}` : undefined,
    });
  }

  const facts: HeaderFact[] = [];
  if (deal.company_name && !subtitleSegments.length) {
    facts.push({ id: "company", icon: Building2, value: deal.company_name });
  }
  const closeDate = formatHeaderDate(deal.close_date);
  if (closeDate) {
    facts.push({
      id: "close-date",
      icon: CalendarClock,
      label: "Close",
      value: closeDate,
      fieldId: "close_date" satisfies DealHeaderEditableField,
    });
  }

  // Whether this deal has been followed up on in the current ISO week. `tags`
  // is `(string | null)[] | null` on the codegen type; drop nulls before the
  // shared helper that expects `string[]`.
  const dealTags = (deal.tags ?? []).filter((tag): tag is string => typeof tag === "string");
  const followedUp = hasBeenFollowedUpThisWeek(dealTags);
  facts.push({
    id: "follow-up",
    icon: CalendarCheck2,
    label: "Follow-up",
    value: followedUp ? "Done this week" : "Needed this week",
  });

  const currency = deal.currency ?? "USD";
  const metrics: HeaderMetric[] = [];
  const revenue = formatCurrency(deal.revenue, { currency });
  if (revenue) {
    metrics.push({
      id: "revenue",
      label: "Revenue",
      value: revenue,
      fieldId: "revenue" satisfies DealHeaderEditableField,
    });
  }

  if (deal.is_recurring) {
    const mrr = formatCurrency(deal.monthly_amount, { currency });
    if (mrr) {
      metrics.push({
        id: "mrr",
        label: "MRR",
        value: mrr,
        hint: "/mo",
        fieldId: "monthly_amount" satisfies DealHeaderEditableField,
      });
    }
  }

  if (deal.close_probability != null) {
    metrics.push({
      id: "probability",
      label: "Probability",
      value: `${deal.close_probability}%`,
      fieldId: "close_probability" satisfies DealHeaderEditableField,
    });
  }

  return { subtitleSegments, facts, metrics };
}
